import { Stack, Typography } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid";
import { type FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FacetFilterRail } from "../../../components/UIToolkit/Filters/FacetFilterRail";
import { FilteredEmptyState } from "../../../components/UIToolkit/Filters/FilteredEmptyState";
import { FilterRailFrame } from "../../../components/UIToolkit/Filters/FilterRailFrame";
import type { FacetSpec } from "../../../components/UIToolkit/Filters/facetSections";
import { SystemValidationTextField } from "../../../components/UIToolkit/Inputs/SystemValidationTextField";
import { EmptyState } from "../../../components/UIToolkit/molecules/EmptyState";
import { SettingsSection } from "../../../components/UIToolkit/molecules/SettingsSection";
import SystemButton from "../../../components/UIToolkit/SystemButton/SystemButton";
import { SystemDataGrid } from "../../../components/UIToolkit/Tables/SystemDataGrid";
import { useFilterRailHandoff, useGridFacetFilters } from "../../../hooks/useFacetFilters";
import { pbjDailyHoursCapSortingKey, pbjLongShiftReviewSortingKey, pbjSingleDayFlagSortingKey } from "../../../services/LocalStorageService";
import type { PbjDailyHoursCapRow, PbjLongShiftRow, PbjModel, PbjModelUpsertBody } from "../../../utils/models";
import type { MultiSortOption } from "../../../utils/multiColumnSorting";
import { usePbjLongShiftReview, usePbjLongShifts, usePbjSubmissionHourCapReport } from "../hooks/usePbjAudit";
import { useUpsertPbjModel } from "../hooks/useUpsertPbjModel";
import type { WithBuilding } from "../network/buildingQueries";
import { usePbjNetwork } from "../network/PbjNetworkContext";
import { useBuildingGridParts, useWithBuildingFacet } from "../network/useBuildingGridParts";
import { dataTableSx, sectionCardSx } from "../PredictionTab/colors";
import { useFacetLabels } from "../pbjFacetLabels";
import { dayTypeFacet, monthFacet } from "../pbjFacets";
import { boldNameColumn, hoursColumn, monospaceIdColumn, sectionFrameHeight } from "../pbjGridCells";
import { formatAuditDate } from "../pbjGridFormat";
import { sortByOtherField } from "../pbjGridSorting";
import { CmsCapBadge } from "../SubmissionSheetsTab/CmsCapBadge";
import { ShiftDurationReviewSection } from "./ShiftDurationReviewSection";

type Props = {
	startDate: string;
	endDate: string;
	/** The dept's saved PBJ Model row — source for the editable Single Day Flag threshold + the upsert body. */
	savedModel: PbjModel;
};

/**
 * Read-only Long Shifts audit — two independent data-quality signals stacked in one tab:
 *
 * 1. **Long Shift Review** (primary): one row per individual shift whose gross clock-in/out duration exceeds a
 *    fixed per-shift threshold (sourced from the response, not hardcoded). Restores overnight shifts the
 *    per-day flag missed (they split at midnight). Renders through the shared ShiftDurationReviewSection —
 *    the same grid the Short Shifts tab uses for the opposite end of the distribution.
 * 2. **Single Day Flag** (existing): one row per (employee, dept-local day) whose summed net staffing hours cross
 *    the editable per-dept threshold. The threshold input lives on THIS section only.
 */
type SingleDayGridRow = WithBuilding<PbjLongShiftRow>;
type CappedDayGridRow = WithBuilding<PbjDailyHoursCapRow>;

const SINGLE_DAY_COLUMN_FACET: Readonly<Record<string, string>> = { jobDescriptions: "role", date: "month" };
const CAPPED_COLUMN_FACET: Readonly<Record<string, string>> = { date: "month" };

export const LongShiftsTable: FC<Props> = ({ startDate, endDate, savedModel }) => {
	const { t } = useTranslation();
	// Two independent endpoints (kept apart so neither response body changes shape): the per-shift
	// review (rows + its fixed threshold) and the per-day Single Day Flag (a bare array).
	const review = usePbjLongShiftReview(startDate, endDate);
	const singleDay = usePbjLongShifts(startDate, endDate);
	// (employee, day) rows trimmed to the CMS 22.5h/day cap in the filed submission — surfaced read-only
	// at the bottom of this tab so every override is auditable (mirrors the submission grid's tooltip).
	const cappedDays = usePbjSubmissionHourCapReport(startDate, endDate);
	const upsert = useUpsertPbjModel();
	const singleDayBuilding = useBuildingGridParts<SingleDayGridRow>();
	const cappedBuilding = useBuildingGridParts<CappedDayGridRow>();
	// The threshold is saved per building; it edits the header department's, so the label names it.
	const { isNetwork, homeDepartmentId, buildingName } = usePbjNetwork();

	// Editable threshold (hours) for the Single Day Flag audit only. Kept as a string for input ergonomics;
	// re-seeded whenever the saved row changes (dept switch / post-save refresh via updateUserInfo).
	const [thresholdInput, setThresholdInput] = useState(String(savedModel.longShiftThreshold));
	useEffect(() => setThresholdInput(String(savedModel.longShiftThreshold)), [savedModel.longShiftThreshold]);

	const parsedThreshold = Number(thresholdInput);
	const thresholdValid = Number.isFinite(parsedThreshold) && parsedThreshold > 0;
	const thresholdDirty = thresholdValid && parsedThreshold !== savedModel.longShiftThreshold;

	const saveThreshold = () => {
		if (!thresholdDirty) return;
		// Merge onto the saved row so no other PBJ Model field is clobbered; the upsert invalidates the
		// audit cache (so this list refilters) and refreshes the saved row via updateUserInfo.
		const body: PbjModelUpsertBody = { ...savedModel, longShiftThreshold: parsedThreshold };
		upsert.mutate(body);
	};

	const singleDayColumns: GridColDef<SingleDayGridRow>[] = useMemo(
		() => [
			...singleDayBuilding.columns,
			{ field: "empId", headerName: t("pbj_model.audit.long_shifts.cols.id"), minWidth: 140, flex: 1 },
			{ field: "name", headerName: t("pbj_model.audit.long_shifts.cols.name"), minWidth: 180, flex: 1 },
			{
				field: "date",
				headerName: t("pbj_model.audit.long_shifts.cols.date"),
				minWidth: 140,
				flex: 1,
				valueFormatter: (value: string) => formatAuditDate(value),
			},
			hoursColumn<SingleDayGridRow>("totalHours", t("pbj_model.audit.long_shifts.cols.total_hours"), {
				minWidth: 140,
				flex: 1,
				align: "left",
				headerAlign: "left",
			}),
			{
				field: "jobDescriptions",
				headerName: t("pbj_model.audit.long_shifts.cols.roles"),
				minWidth: 220,
				flex: 2,
				// DEFAULT: guard the join — the BE always sends jobDescriptions as an array (possibly []),
				// but a single cell getter must never throw and tear down the whole Audit tab via the error boundary.
				valueGetter: (_value, row) => (row.jobDescriptions ?? []).join(", "),
			},
		],
		[t, singleDayBuilding.columns],
	);

	const singleDaySortingOptions = useMemo<MultiSortOption<SingleDayGridRow>[]>(
		() => [
			...singleDayBuilding.sortFields.map(({ field, label }) => ({ label, sortColumns: [field] })),
			{ label: t("pbj_model.audit.long_shifts.cols.id"), sortColumns: ["empId"] },
			{ label: t("pbj_model.audit.long_shifts.cols.name"), sortColumns: ["name"] },
			{ label: t("pbj_model.audit.long_shifts.cols.date"), sortColumns: ["date"] },
			{ label: t("pbj_model.audit.long_shifts.cols.total_hours"), sortColumns: ["totalHours"] },
			sortByOtherField<SingleDayGridRow>("jobDescriptions", t("pbj_model.audit.long_shifts.cols.roles"), (r) => r.jobDescriptions.join(", ")),
		],
		[t, singleDayBuilding.sortFields],
	);

	const labels = useFacetLabels();
	const singleDayRows = useMemo(() => singleDay.data ?? [], [singleDay.data]);
	const singleDayFacets = useMemo<FacetSpec<SingleDayGridRow>[]>(
		() => [
			...singleDayBuilding.facets,
			{
				dimension: { id: "role", getKeys: (r: SingleDayGridRow) => r.jobDescriptions },
				title: t("pbj_model.audit.long_shifts.cols.roles"),
				getLabel: labels.raw,
				searchable: true,
			},
			{ dimension: monthFacet<SingleDayGridRow>("month", (r) => r.date), title: t("pbj_model.filter.month"), getLabel: labels.month, order: "key" },
			{ dimension: dayTypeFacet<SingleDayGridRow>("day_type", (r) => r.date), title: t("pbj_model.filter.day_type"), getLabel: labels.dayType },
		],
		[t, labels, singleDayBuilding.facets],
	);
	const singleDayFilters = useGridFacetFilters(singleDayRows, singleDayFacets, singleDayBuilding.sharedGroups);
	const singleDayRail = useFilterRailHandoff(singleDayFilters.highlight, useWithBuildingFacet(SINGLE_DAY_COLUMN_FACET), t("pbj_model.filter.in_rail"));

	// "Filed Hours" renders the capped value (the fixed cap, e.g. 22.5) with a tooltip citing the real
	// worked hours — the same CmsCapBadge the submission grid uses, so the two surfaces read identically.
	const cappedDayColumns: GridColDef<CappedDayGridRow>[] = useMemo(
		() => [
			...cappedBuilding.columns,
			monospaceIdColumn<CappedDayGridRow>("empId", t("pbj_model.audit.long_shifts.cols.id"), { minWidth: 140, flex: 1 }),
			boldNameColumn<CappedDayGridRow>("name", t("pbj_model.audit.long_shifts.cols.name"), { minWidth: 180, flex: 1 }),
			{
				field: "date",
				headerName: t("pbj_model.audit.long_shifts.cols.date"),
				minWidth: 140,
				flex: 1,
				valueFormatter: (value) => formatAuditDate(value),
			},
			hoursColumn<CappedDayGridRow>("originalHours", t("pbj_model.audit.long_shifts.cols.original_hours"), {
				minWidth: 140,
				flex: 1,
				align: "left",
				headerAlign: "left",
			}),
			{
				field: "cappedHours",
				headerName: t("pbj_model.audit.long_shifts.cols.filed_hours"),
				minWidth: 160,
				flex: 1,
				type: "number",
				align: "left",
				headerAlign: "left",
				// Sort on the numeric value, but render the badge (capped value + tooltip citing the real hours).
				renderCell: (params) => (
					<CmsCapBadge cappedHours={params.row.cappedHours} originalHours={params.row.originalHours} dataTestId="pbj-daily-hours-cap-badge" />
				),
			},
		],
		[t, cappedBuilding.columns],
	);

	const cappedDaySortingOptions = useMemo<MultiSortOption<CappedDayGridRow>[]>(
		() => [
			...cappedBuilding.sortFields.map(({ field, label }) => ({ label, sortColumns: [field] })),
			{ label: t("pbj_model.audit.long_shifts.cols.id"), sortColumns: ["empId"] },
			{ label: t("pbj_model.audit.long_shifts.cols.name"), sortColumns: ["name"] },
			{ label: t("pbj_model.audit.long_shifts.cols.date"), sortColumns: ["date"] },
			{ label: t("pbj_model.audit.long_shifts.cols.original_hours"), sortColumns: ["originalHours"] },
			{ label: t("pbj_model.audit.long_shifts.cols.filed_hours"), sortColumns: ["cappedHours"] },
		],
		[t, cappedBuilding.sortFields],
	);

	const cappedRows = useMemo(() => cappedDays.data?.rows ?? [], [cappedDays.data]);
	const cappedFacets = useMemo<FacetSpec<CappedDayGridRow>[]>(
		() => [
			...cappedBuilding.facets,
			{ dimension: monthFacet<CappedDayGridRow>("month", (r) => r.date), title: t("pbj_model.filter.month"), getLabel: labels.month, order: "key" },
			{ dimension: dayTypeFacet<CappedDayGridRow>("day_type", (r) => r.date), title: t("pbj_model.filter.day_type"), getLabel: labels.dayType },
		],
		[t, labels, cappedBuilding.facets],
	);
	const cappedFilters = useGridFacetFilters(cappedRows, cappedFacets, cappedBuilding.sharedGroups);
	const cappedRail = useFilterRailHandoff(cappedFilters.highlight, useWithBuildingFacet(CAPPED_COLUMN_FACET), t("pbj_model.filter.in_rail"));

	return (
		<Stack spacing={2.5} data-testid="pbj-long-shifts-tab">
			<ShiftDurationReviewSection
				title={t("pbj_model.audit.long_shifts.review.title")}
				// Threshold comes from the response (undefined while loading), so only show the number-bearing
				// subtitle once loaded — avoids an "exceeds  hours" flash and keeps the value single-sourced on the BE.
				description={review.data ? t("pbj_model.audit.long_shifts.review.subtitle", { hours: review.data.thresholdHours }) : undefined}
				emptyTitle={t("pbj_model.audit.long_shifts.review.empty", { hours: review.data?.thresholdHours })}
				errorMessage={t("pbj_model.audit.long_shifts.review.failed")}
				rows={review.data?.rows ?? []}
				loading={review.isLoading}
				error={review.isError}
				sectionTestId="pbj-long-shift-review-section"
				tableTestId="pbj-long-shift-review-table"
				emptyTestId="pbj-long-shift-review-empty"
				sortingLocalStorageKey={pbjLongShiftReviewSortingKey}
			/>

			<SettingsSection
				layout="stack"
				title={t("pbj_model.audit.long_shifts.single_day.title")}
				description={t("pbj_model.audit.long_shifts.single_day.subtitle")}
				dataTestId="pbj-single-day-flag-section"
				sx={(theme) => sectionCardSx(theme)}
			>
				<FilterRailFrame
					flat
					height={sectionFrameHeight(singleDayRows.length)}
					dataTestId="pbj-single-day-flag-filters-frame"
					rail={
						<FacetFilterRail
							rows={singleDayRows}
							facets={singleDayFacets}
							filters={singleDayFilters}
							collapsed={singleDayRail.railCollapsed}
							onCollapsedChange={singleDayRail.setRailCollapsed}
							dataTestId="pbj-single-day-flag-filters"
						/>
					}
				>
					<SystemDataGrid<SingleDayGridRow>
						rows={singleDayFilters.result.rows}
						columns={singleDayColumns}
						exportOptions={{ fileName: t("pbj_model.audit.long_shifts.single_day.title") }}
						getRowId={(r) => `${r.buildingId}-${r.empId}-${r.date}`}
						canFilterByColumn={singleDayRail.canFilterByColumn}
						onFilterByColumn={singleDayRail.onFilterByColumn}
						filterByHint={singleDayRail.filterByHint}
						sortingOptions={singleDaySortingOptions}
						showSortSummary
						localStorageKey={pbjSingleDayFlagSortingKey}
						searchableColumns={["empId", "name"]}
						loading={singleDay.isLoading}
						error={singleDay.isError}
						errorMessage={t("pbj_model.audit.long_shifts.failed")}
						emptyState={
							<FilteredEmptyState
								filtered={singleDayFilters.activeCount > 0}
								onClearFilters={singleDayFilters.clear}
								unfilteredState={<EmptyState title={t("pbj_model.audit.long_shifts.empty")} dataTestId="pbj-single-day-flag-empty" />}
								dataTestId="pbj-single-day-flag-filtered-empty"
							/>
						}
						defaultPageSize={25}
						sx={(theme) => dataTableSx(theme)}
						dataTestId="pbj-single-day-flag-table"
						trailingToolbarNodes={
							<Stack direction="row" spacing={1} sx={{ alignItems: "center", ml: "auto" }}>
								<Typography variant="body2" sx={{ color: "text.secondary", whiteSpace: "nowrap" }}>
									{isNetwork
										? t("pbj_model.audit.long_shifts.threshold_label_for_building", { building: buildingName(homeDepartmentId) })
										: t("pbj_model.audit.long_shifts.threshold_label")}
								</Typography>
								<SystemValidationTextField
									inputType="number"
									value={thresholdInput}
									onChange={(e) => setThresholdInput(e.target.value)}
									reserveHeightForHelperText={false}
									formControlSx={{ width: 96 }}
									injectedProps={{ slotProps: { htmlInput: { min: 0, step: 0.5 } } }}
									dataTestId="pbj-long-shifts-threshold-input"
								/>
								<SystemButton
									buttonVariant={thresholdDirty ? "primary" : "secondary"}
									size="small"
									onClick={saveThreshold}
									disabled={!thresholdDirty}
									loading={upsert.isPending}
									noFullWidthMobile
									data-testid="pbj-long-shifts-threshold-save"
								>
									{t("pbj_model.audit.long_shifts.threshold_save")}
								</SystemButton>
							</Stack>
						}
					/>
				</FilterRailFrame>
			</SettingsSection>

			<SettingsSection
				layout="stack"
				title={t("pbj_model.audit.long_shifts.daily_cap.title")}
				// Cap comes from the response (undefined while loading) so the number-bearing subtitle only shows
				// once loaded — avoids a "capped to  hours" flash and keeps the value single-sourced on the BE.
				description={cappedDays.data ? t("pbj_model.audit.long_shifts.daily_cap.subtitle", { hours: cappedDays.data.capHours }) : undefined}
				dataTestId="pbj-daily-hours-cap-section"
				sx={(theme) => sectionCardSx(theme)}
			>
				<FilterRailFrame
					flat
					height={sectionFrameHeight(cappedRows.length)}
					dataTestId="pbj-daily-hours-cap-filters-frame"
					rail={
						<FacetFilterRail
							rows={cappedRows}
							facets={cappedFacets}
							filters={cappedFilters}
							collapsed={cappedRail.railCollapsed}
							onCollapsedChange={cappedRail.setRailCollapsed}
							dataTestId="pbj-daily-hours-cap-filters"
						/>
					}
				>
					<SystemDataGrid<CappedDayGridRow>
						rows={cappedFilters.result.rows}
						columns={cappedDayColumns}
						exportOptions={{ fileName: t("pbj_model.audit.long_shifts.daily_cap.title") }}
						getRowId={(r) => `${r.buildingId}-${r.empId}-${r.date}`}
						canFilterByColumn={cappedRail.canFilterByColumn}
						onFilterByColumn={cappedRail.onFilterByColumn}
						filterByHint={cappedRail.filterByHint}
						sortingOptions={cappedDaySortingOptions}
						showSortSummary
						localStorageKey={pbjDailyHoursCapSortingKey}
						searchableColumns={["empId", "name"]}
						loading={cappedDays.isLoading}
						error={cappedDays.isError}
						errorMessage={t("pbj_model.audit.submission_hour_cap_report.failed")}
						emptyState={
							<FilteredEmptyState
								filtered={cappedFilters.activeCount > 0}
								onClearFilters={cappedFilters.clear}
								unfilteredState={
									<EmptyState
										title={t("pbj_model.audit.long_shifts.daily_cap.empty", { hours: cappedDays.data?.capHours })}
										dataTestId="pbj-daily-hours-cap-empty"
									/>
								}
								dataTestId="pbj-daily-hours-cap-filtered-empty"
							/>
						}
						defaultPageSize={25}
						sx={(theme) => dataTableSx(theme)}
						dataTestId="pbj-daily-hours-cap-table"
					/>
				</FilterRailFrame>
			</SettingsSection>
		</Stack>
	);
};
